import { getAWSFile, getPresignedS3DownloadUrl } from '@/actions/files/s3'
import { toast } from 'react-hot-toast'

/**
 * Downloads a file from S3 using the GF API as a proxy.
 */
export async function downloadFile({
  /**
   * The S3 key for the file.
   * - Triggers a `getObject` download from S3.
   * @type {string}
   */
  s3Key,
  /**
   * The S3 URL for the file.
   * - Triggers a `fetch` download from the S3 URL.
   * @type {string}
   */
  s3Url,
  /**
   * The database id
   * @type {string}
   */
  id,
  /**
   * The database prisma model key
   * @type {string}
   */
  key,
  /**
   * Name of the downloaded file
   * @type {string}
   */
  name,
  /**
   * File extension for the downloaded file
   * @type {string}
   */
  ext,
  /**
   * Suppresses the toast messages
   * @type {boolean}
   */
  suppressToast = false,
}) {
  const toastId = toast.loading('Downloading File...')

  let blob

  if (s3Url || s3Key) {
    try {
      const resolvedUrl = s3Url || (await getPresignedS3DownloadUrl(s3Key))
      const response = await fetch(resolvedUrl)
      if (!response.ok) {
        throw new Error(`Failed to download file. Status: ${response.status}`)
      }
      blob = await response.blob()
    } catch (e) {
      console.error(e)
      if (!suppressToast)
        toast.error('Could not download file. Check logs.', {
          id: toastId,
        })
    }
  } else {
    if (!key) {
      if (!suppressToast)
        toast.error('Unable to download file. Missing file type.', {
          id: toastId,
        })
      return
    }

    if (!name) {
      if (!suppressToast)
        toast.error('Unable to download file. Missing file name.', {
          id: toastId,
        })
      return
    }

    if (!ext) {
      if (!suppressToast)
        toast.error('Unable to download file. Missing file extension.', {
          id: toastId,
        })
      return
    }

    try {
      const response = await getAWSFile(id, key)
      const responseUrl = response?.file?.url
      if (!responseUrl) {
        throw new Error('Missing file url')
      }
      const fileResponse = await fetch(responseUrl)
      if (!fileResponse.ok) {
        throw new Error(
          `Failed to download file. Status: ${fileResponse.status}`,
        )
      }
      blob = await fileResponse.blob()
    } catch (e) {
      console.error(e)
      if (!suppressToast)
        toast.error('Could not download file. Check logs.', {
          id: toastId,
        })
    }
  }

  if (!blob) {
    return
  }

  const blobUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.style.display = 'none'
  a.href = blobUrl
  a.download = name + '.' + ext
  document.body.appendChild(a)
  a.click()
  window.URL.revokeObjectURL(blobUrl)

  if (!suppressToast) {
    toast.success('Your download should have started!', {
      id: toastId,
    })
  }
}
